/**
 * activeFrame state — persists across reloads via gibbs/preferences.
 *
 * Single-frame v1 (per the brief): only one frame active at a time;
 * setting a new one replaces the prior. Clearing returns to no-frame.
 *
 * @module gibbs/question-chooser/activeFrameState
 */

import { getPreference, savePreference } from '../preferences';
import { getFrameById, isEscapeFrame } from './frames';

const ACTIVE_FRAME_PREF_KEY = 'activeFrame';

/**
 * Read the currently-active frame id. Returns null when no frame
 * is set OR when the user explicitly picked the escape-hatch.
 *
 * @returns {string|null}
 */
export function getActiveFrameId() {
    const v = getPreference(ACTIVE_FRAME_PREF_KEY, null);
    if (!v) return null;
    if (isEscapeFrame(v)) return null;
    return v;
}

/**
 * Read the full active frame object, or null if none active.
 *
 * @returns {import('./frames').Frame|null}
 */
export function getActiveFrame() {
    const id = getActiveFrameId();
    return getFrameById(id);
}

/**
 * Persist a frame choice. Pass null to clear the active frame
 * back to no-frame. Passing the escape-hatch id is equivalent to
 * clearing.
 *
 * @param {string|null} id
 * @returns {Promise<{ success: boolean, error: string|null }>}
 */
export async function setActiveFrame(id) {
    if (id == null || isEscapeFrame(id)) {
        return savePreference(ACTIVE_FRAME_PREF_KEY, '');
    }
    const frame = getFrameById(id);
    if (!frame) {
        return {
            success: false,
            error: `unknown_frame: ${id}`,
        };
    }
    return savePreference(ACTIVE_FRAME_PREF_KEY, id);
}

/**
 * Clear any active frame.
 *
 * @returns {Promise<{ success: boolean, error: string|null }>}
 */
export async function clearActiveFrame() {
    return setActiveFrame(null);
}
